import { useMemo } from "react";

import { keepPreviousData } from "@tanstack/react-query";
import { useHistory } from "react-router";

import { Icon, Text } from "@alpacahq/ui/old";

import {
  useGetIsLegalEntity,
  useGetOwners,
  useGetTradingAccount,
} from "~/v2/api/hooks";
import { OnboardingLayout } from "~/v2/components/onboarding";
import { Path } from "~/v2/path";
import type { EntityOnboardingHistoryState } from "../../constants";

const YOUTUBE_LINK = "https://www.youtube.com/watch?v=B0Z7oCmr5nM";
const DOCS_LINK = "https://docs.alpaca.markets/docs/trading-api";
const LEARN_LINK = "https://alpaca.markets/learn/search?category=trading-api";

export const CompletedPage = () => {
  const history = useHistory<EntityOnboardingHistoryState>();

  const { account } = useGetTradingAccount();

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const { owners } = useGetOwners({
    accountId: account?.id || "",
    options: {
      placeholderData: keepPreviousData,
      enabled: !!account?.id,
    },
  });

  const { legalEntity, accountOpener, ubos } = useMemo(
    () => ({
      legalEntity: owners?.find((details) => details.is_legal_entity),
      accountOpener: owners?.find((details) => details.is_legal_account_opener),
      ubos: owners?.filter((details) => details.is_ubo) ?? [],
    }),
    [owners],
  );

  if (account && !isLegalEntity && !isLegalEntityLoading) {
    history.replace(Path.ROUTE_NEW_ACCOUNT);
  }

  const isLoading =
    !account?.id ||
    !legalEntity?.owner_id ||
    !accountOpener?.owner_id ||
    !ubos.length;

  return (
    <OnboardingLayout
      title="Application Complete!"
      percentage={100}
      isLoading={isLoading}
    >
      <Text className="mb-5 text-lg">
        Thank you for submitting your business account application. Business
        accounts require a manual review to ensure all required information and
        documentation is received and verified. Please allow 3 to 5 business
        days for this manual review.
      </Text>
      <Text className="mb-6 text-lg">
        If we need additional information to complete our review, we will
        contact you.
      </Text>
      <div className="flex flex-col gap-8 rounded border border-gray-20 bg-cool-gray-50 p-5">
        <Text className="font-medium text-lg">Want to learn more?</Text>
        <div>
          <Text className="mb-4 font-medium">Youtube</Text>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Text className="text-gray-700">
              Learn how to configure your environment, create orders, and
              monitor your orders, trades and positions with Alpaca's Trading
              API.
            </Text>
            <a
              className="flex min-w-44 items-center justify-center space-x-2 whitespace-nowrap rounded-lg border border-yellow-300 bg-yellow-50 p-2.5 text-center font-medium text-base text-yellow-900 focus:outline-none focus:ring-4 focus:ring-yellow-200"
              href={YOUTUBE_LINK}
              target="_blank"
              rel="noopener"
            >
              Watch Tutorial
              <Icon name="ArrowRight" className="ml-3 text-yellow-900" />
            </a>
          </div>
        </div>
        <div>
          <Text className="mb-4 font-medium">API Reference</Text>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Text className="text-gray-700">
              Check out our reference documentation for Trading API, including
              examples and code snippets for our endpoints in Node.js, Ruby,
              PHP, Python and more.
            </Text>
            <a
              className="flex min-w-44 items-center justify-center space-x-2 whitespace-nowrap rounded-lg border border-yellow-300 bg-yellow-50 p-2.5 text-center font-medium text-base text-yellow-900 focus:outline-none focus:ring-4 focus:ring-yellow-200"
              href={DOCS_LINK}
              target="_blank"
              rel="noopener"
            >
              API Reference
              <Icon name="ArrowRight" className="ml-3 text-yellow-900" />
            </a>
          </div>
        </div>
        <div>
          <Text className="mb-4 font-medium">Learn</Text>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <Text className="text-gray-700">
              Explore our Learn page for expert insights, helpful guides, and
              powerful strategies to boost your trading success.
            </Text>
            <a
              className="flex min-w-44 items-center justify-center space-x-2 whitespace-nowrap rounded-lg border border-yellow-300 bg-yellow-50 p-2.5 text-center font-medium text-base text-yellow-900 focus:outline-none focus:ring-4 focus:ring-yellow-200"
              href={LEARN_LINK}
              target="_blank"
              rel="noopener"
            >
              Learn
              <Icon name="ArrowRight" className="ml-3 text-yellow-900" />
            </a>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default CompletedPage;
